// src/app/core/services/translation-service.ts
import { Injectable, Inject, Optional } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { TRANSLATION_CONFIG, TranslationConfig, LanguageInfo } from '../translation.config';

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private readonly languages: LanguageInfo[];
  private readonly defaultLang: string;
  private readonly useUrl: boolean;

  private lang$ = new BehaviorSubject<string>('en');

  constructor(
    private router: Router,
    @Optional() @Inject(TRANSLATION_CONFIG) cfg?: TranslationConfig
  ) {
    this.languages = cfg?.languages ?? [{ code: 'en', name: 'English' }];
    this.defaultLang = cfg?.default ?? this.languages[0].code;
    this.useUrl = !!cfg?.useUrl;
  }

  /** Call once in AppComponent.ngOnInit() */
  init(): void {
    const initial = this.resolveInitialLang();
    this.lang$.next(initial);
    if (this.useUrl) this.ensureUrlLang(initial);
  }

  getLanguage(): string { return this.lang$.value; }
  languageChanges$(): Observable<string> { return this.lang$.asObservable(); }
  getSupported(): string[] { return this.languages.map(lang => lang.code); }
  getLanguages(): LanguageInfo[] { return [...this.languages]; }
  getLanguageName(code: string): string {
    return this.languages.find(lang => lang.code === code)?.name || code.toUpperCase();
  }
  getCurrentLanguageName(): string {
    return this.getLanguageName(this.getLanguage());
  }
  isSupported(lang: string): boolean { return this.languages.some(l => l.code === lang); }

  setLanguage(lang: string, opts: { updateUrl?: boolean } = {}): void {
    const normalized = this.normalize(lang);
    if (normalized !== this.lang$.value) this.lang$.next(normalized);

    if (this.useUrl && opts.updateUrl !== false) {
      const parts = this.router.url.split('?')[0].split('/').filter(Boolean);
      const rest = this.languages.some(l => l.code === parts[0])
        ? parts.slice(1)
        : parts;

      this.router.navigate([normalized, ...rest], {
        queryParamsHandling: 'preserve',
        replaceUrl: true,
      });
    }
  }

  // ===== internals =====
  private resolveInitialLang(): string {
    const fromUrl = this.useUrl ? this.langFromUrl() : null;
    return this.normalize(fromUrl || this.defaultLang);
  }

  private langFromUrl(): string | null {
    const path = this.router.url.split('?')[0];
    const first = path.split('/').filter(Boolean)[0];
    return first ?? null;
  }

  private ensureUrlLang(lang: string): void {
    const pathParts = this.router.url.split('?')[0].split('/').filter(Boolean);
    const currentPrefix = pathParts[0];
    if (currentPrefix !== lang) {
      const rest = currentPrefix && this.languages.some(l => l.code === currentPrefix)
        ? pathParts.slice(1)
        : pathParts;
      this.router.navigate(['/', lang, ...rest], {
        queryParamsHandling: 'preserve',
        replaceUrl: true,
      });
    }
  }

  private normalize(lang: string | null): string {
    return this.languages.some(l => l.code === lang) ? (lang as string) : this.defaultLang;
  }
}
